// J-238 (group access, release A; the order of 2026-10-16 02:59Z item 3): a user whose `/me/access`
// says `scopeKind: "group"` sees only its granted performance groups. These are the web's rules for
// such a user: where it lands, which groups a screen offers, and which group a URL may carry. The
// server stays the authority: it answers 403 for another group and for every ACO-wide route.
import type { GroupGrant, MyAccess } from "@vbltc/schema";

type ScopeFields = "scopeKind" | "groups";
/** `/me/access` as the wire carries it: an older server sends neither scope field. */
type WireAccess = Omit<MyAccess, ScopeFields> & { [K in ScopeFields]?: MyAccess[K] | undefined };

/** The access with both scope fields present: an older server's answer is an ACO-set user's. */
export function withGroupScope(access: WireAccess): MyAccess {
  return { ...access, scopeKind: access.scopeKind ?? "aco-set", groups: access.groups ?? [] };
}

/** True when the caller sees only its granted groups. False while `/me/access` has not answered. */
export const isGroupScoped = <T extends Pick<MyAccess, "scopeKind">>(
  access: T | undefined,
): access is T & { scopeKind: "group" } => access?.scopeKind === "group";

/**
 * J-251 (the owner's order item 9): true when the caller sees only its own groups or practices and no
 * ACO-wide data. False while `/me/access` has not answered.
 */
export const isOwnScoped = (access: Pick<MyAccess, "scopeKind"> | undefined): boolean =>
  access?.scopeKind === "group" || access?.scopeKind === "practice";

/**
 * J-366 (Louis, 2026-10-27): true when the caller is practice-scoped and `/me/access` lists groups it
 * sees (J-278: a granted group, or the group of a practice in its scope). Quality Detail offers it their
 * filter; the server serves only those groups and narrows every read to its practices.
 */
export const seesPracticeGroups = (
  access: Pick<MyAccess, "scopeKind" | "groups"> | undefined,
): boolean => access?.scopeKind === "practice" && access.groups.length > 0;

/** The ACO selector's "All" value (`features/aco-shell/default-aco.ts` ALL_ACOS; lib imports no feature). */
const ALL_ACOS_VALUE = "ALL";

/**
 * J-301 (QB4 item 3a): the URL names "All" (or no ACO, which the shell reads as All). For a group or
 * practice user it is every ACO of its own groups or practices; the server narrows every read to them.
 */
const namesAll = (aco: string | undefined) => aco === undefined || aco === ALL_ACOS_VALUE;

/** The ACO codes of the grants, first-granted first, each once (the header's ACO list). */
export function grantedAcoCodes(groups: readonly GroupGrant[]): string[] {
  return [...new Set(groups.map((g) => g.acoCode))];
}

/**
 * The granted groups of one ACO and year, in display order. J-301: for "All", every ACO's granted groups
 * of the year, each group once (a group whose practices span ACOs is granted once per ACO).
 */
export function grantedGroupsFor(
  groups: readonly GroupGrant[],
  aco: string | undefined,
  year: number | undefined,
): GroupGrant[] {
  const ofYear = groups.filter((g) => g.performanceYear === year);
  if (aco !== ALL_ACOS_VALUE) return ofYear.filter((g) => g.acoCode === aco);
  return ofYear.filter((g, i) => ofYear.findIndex((x) => x.groupId === g.groupId) === i);
}

/**
 * The Quality report with the first granted group: the page a group user lands on. Undefined
 * without a grant (the caller then lands where an account with nothing to open lands).
 */
export function groupLandingPath(groups: readonly GroupGrant[]): string | undefined {
  const first = groups[0];
  if (first === undefined) return undefined;
  const query = new URLSearchParams({
    aco: first.acoCode,
    year: String(first.performanceYear),
    group: String(first.groupId),
  });
  return `/practice/gaps?${query.toString()}`;
}

interface GroupSearch {
  aco?: string | undefined;
  year?: number | undefined;
  group?: number | undefined;
}

/**
 * The ACO, year and group a group user's URL is replaced with, or undefined when the URL already names
 * a granted group of its ACO and year. A missing or other group becomes the first grant of the URL's
 * ACO and year, else of its ACO, else the first grant of all; the ACO and year follow that grant.
 * J-301 (QB4 item 3a): "All" (or no ACO) stays All, with a granted group of the year in any ACO.
 */
export function grantedGroupSearch(
  groups: readonly GroupGrant[],
  search: GroupSearch,
): Required<GroupSearch> | undefined {
  if (namesAll(search.aco)) {
    const ofYear = groups.filter((g) => g.performanceYear === search.year);
    if (search.aco !== undefined && ofYear.some((g) => g.groupId === search.group))
      return undefined;
    const pick = ofYear[0] ?? groups[0];
    if (pick === undefined) return undefined;
    return { aco: ALL_ACOS_VALUE, year: pick.performanceYear, group: pick.groupId };
  }
  const sameAco = groups.filter((g) => g.acoCode === search.aco);
  const sameYear = sameAco.filter((g) => g.performanceYear === search.year);
  if (sameYear.some((g) => g.groupId === search.group)) return undefined;
  const pick = sameYear[0] ?? sameAco[0] ?? groups[0];
  if (pick === undefined) return undefined;
  return { aco: pick.acoCode, year: pick.performanceYear, group: pick.groupId };
}
